import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SeV2QuestionDto, usePublishInformasjonMutation } from "../../../../autogen/bff-api";
import { AnonymizationBanner } from "../../../../common/components/communication/AnonymizationBanner";
import { richTextHasContent } from "../../../../common/components/communication/communicationUtils";
import { useApiError } from "../../../../common/errors/useApiError";
import { TipTap } from "../../../../common/input/TipTap/TipTap";

interface Props {
  eventId: string;
  fromQuestion?: SeV2QuestionDto | null;
  isOpen: boolean;
  onClose: () => void;
}

export const PublishInformasjonModal = ({ eventId, fromQuestion, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const isQa = !!fromQuestion;
  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [publish, { isLoading }] = usePublishInformasjonMutation();

  useEffect(() => {
    if (isOpen) {
      // Q&A: prefill the question (to anonymize) and the answer separately. Standalone: a single message.
      setTitle(fromQuestion ? fromQuestion.subject : "");
      setQuestion(fromQuestion?.content ?? "");
      setAnswer(fromQuestion?.answer?.content ?? "");
      setResetKey((key) => key + 1);
    }
  }, [isOpen, fromQuestion]);

  const canSubmit =
    title.trim().length > 0 && richTextHasContent(answer) && (!isQa || richTextHasContent(question));

  const handlePublish = async () => {
    if (!canSubmit) return;
    try {
      await publish({
        eventId,
        createInformasjonRequest: {
          title: title.trim(),
          content: answer,
          fromQuestionId: fromQuestion?.id ?? null,
          question: isQa ? question : null,
        },
      }).unwrap();
      onClose();
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Information to tenderers")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <AnonymizationBanner />
            <FormControl isRequired>
              <FormLabel>{t("Title")}</FormLabel>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("Title") ?? ""} />
            </FormControl>
            {isQa && (
              <FormControl isRequired>
                <FormLabel>{t("Question")}</FormLabel>
                <TipTap
                  key={`q-${resetKey}`}
                  content={question}
                  editable
                  debounceWait={0}
                  onUpdate={(value) => setQuestion(value)}
                />
              </FormControl>
            )}
            <FormControl isRequired>
              <FormLabel>{isQa ? t("Answer") : t("Message")}</FormLabel>
              <TipTap
                key={`a-${resetKey}`}
                content={answer}
                editable
                debounceWait={0}
                onUpdate={(value) => setAnswer(value)}
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" onClick={handlePublish} isLoading={isLoading} isDisabled={!canSubmit}>
            {t("Publish to all tenderers")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
